import './assets/css/fonts.css';
import './assets/css/theme-variables.css';
import './assets/css/botco-theme.css';
import './assets/css/ReactToastify.css';
import './App.css';

import { useAuth } from '@workos-inc/authkit-react';
import * as React from 'react';
import { QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Switch, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { Box } from '@botco/library';
import { initFBSDK } from '~/utils';
import { queryClient } from '~/utils/queryClient';

import { Layout } from './components/Layout/Layout';
import { SpinnerLoader } from './components/SpinnerLoader';
import { routes } from './constants/routes';
import { SignIn } from './containers/Auth/SignIn';
import { WorkOSSwitchOrganization } from './containers/Settings/WorkOS/SwitchOrganization';

const AppContent = () => {
  const [isUserLoggedIn, setIsUserLoggedIn] = React.useState<boolean | null>(
    null
  );
  const { user, isLoading, getAccessToken } = useAuth();
  const location = useLocation();

  const getReturnTo = React.useCallback(() => {
    if (
      location.pathname === routes.signOut.path ||
      location.pathname === routes.switchOrganization.path
    ) {
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    if (!urlParams.has('returnTo')) {
      return location.pathname + location.search;
    }
    return urlParams.get('returnTo');
  }, [location]);

  React.useEffect(() => {
    const isLoggedIn = async () => {
      if (isLoading) return;

      if (!user) {
        setIsUserLoggedIn(false);
        return;
      }

      try {
        const token = await getAccessToken();
        if (token) {
          localStorage.setItem('token', token);
          setIsUserLoggedIn(true);
          initFBSDK();
        } else {
          setIsUserLoggedIn(false);
        }
      } catch {
        setIsUserLoggedIn(false);
      }
    };

    isLoggedIn();
  }, [user, isLoading, getAccessToken]);

  if (isLoading || isUserLoggedIn === null) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <SpinnerLoader />
      </Box>
    );
  }

  if (!user) {
    return <SignIn returnTo={getReturnTo()} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Switch>
        <Route
          path={routes.switchOrganization.path}
          component={WorkOSSwitchOrganization}
        />
        <Route path={routes.signOut.path} component={SignIn} />
        <Layout />
      </Switch>
      <ToastContainer />
    </QueryClientProvider>
  );
};

export const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};
